/** @module components/visitors */


import {Subscription} from '@lernetz/ts-lib/src/core/signal/Signal';

import {ChoiceQuiz, ChoiceQuizVisitor} from '../choice-quiz';
import {ClozeQuiz, ClozeQuizVisitor, SelectSpan, SelectSpanChoice} from '../cloze-quiz';
import {DragAndDropQuiz, DragAndDropQuizVisitor} from '../drag-and-drop-quiz';
import {HotSpotQuiz, HotSpotQuizVisitor} from '../hot-spot-quiz';
import {QuizVisitor, canShowCorrectness} from '../quiz';


/**
 * Visitor that will set [[CanShowCorrectness.showCorrectness]] of all answers (choices) given by the user.
 *
 * Because given answers can change over time, this visitor will have to observe all answers (choices)
 * to be able to keep `showCorrectness` properly in sync. The visitor will therefore return a `Subscription`
 * that you can use to tell it to stop observing.
 *
 * If you want this visitor to set `showCorrectness` only for the current state, and not keep updating it,
 * perform an unsubscribe immediately.
 *
 * @example
 *```typescript
 *
 * const showCorrectnessSyncing = new ShowCorrectnessOfGivenAnswersQuizVisitor().visit(quiz);
 * ...
 * showCorrectnessSyncing.unsubscribe();
 *```
 */
export class ShowCorrectnessOfGivenAnswersQuizVisitor extends QuizVisitor<Subscription> implements ChoiceQuizVisitor, ClozeQuizVisitor, DragAndDropQuizVisitor, HotSpotQuizVisitor {

    public visitChoiceQuiz(quiz: ChoiceQuiz): Subscription {

        const subscriptions: Subscription[] = [];

        for (const choice of quiz.choices) {
            if (canShowCorrectness(choice)) {
                subscriptions.push(choice.change.filter(({ name }) => name === 'selected')
                                                .subscribe(() => choice.showCorrectness = choice.selected));
                choice.showCorrectness = choice.selected;
            }
        }

        return {
            unsubscribe: function() {
                for (const subscription of subscriptions) {
                    subscription.unsubscribe();
                }
            }
        }
    }

    public visitClozeQuiz(quiz: ClozeQuiz): Subscription {

        const subscriptions: Subscription[] = [];

        for (const paragraph of quiz.paragraphs) {
            for (const span of paragraph.spans) {
                if (span instanceof SelectSpan) {
                    if (canShowCorrectness(span)) {
                        span.showCorrectness = true;
                    }
                    for (const choice of span.choices) {
                        subscriptions.push(choice.change.filter(({ name }) => name === 'selected')
                                                        .subscribe(() => choice.showCorrectness = choice.selected));
                        choice.showCorrectness = choice.selected;
                    }
                }
            }
        }

        return {
            unsubscribe: function() {
                for (const subscription of subscriptions) {
                    subscription.unsubscribe();
                }
            }
        }
    }

    public visitDragAndDropQuiz(quiz: DragAndDropQuiz): Subscription {

        const subscriptions: Subscription[] = [];

        for (const dropArea of quiz.dropAreas) {
            for (const item of dropArea.items) {
                if (canShowCorrectness(item)) {
                    item.showCorrectness = true;
                }
            }

            // TODO: this is a hack to trigger a DOM update:
            dropArea.change.dispatch({ name: 'items', newValue: dropArea.items, oldValue: dropArea.items });
        }

        return {
            unsubscribe: function() {
                for (const subscription of subscriptions) {
                    subscription.unsubscribe();
                }
            }
        };
    }

    public visitHotSpotQuiz(quiz: HotSpotQuiz): Subscription {

        function update(): void {
            for (const spot of quiz.spots) {
                if (canShowCorrectness(spot)) {
                    spot.showCorrectness = true;
                }
            }
        }

        update();
        return quiz.change.filter(({ name }) => name === 'spots')
                          .subscribe(() => update());
    }
}
